import store from '@/store';
import { Actions } from '@/store/enums/StoreEnums';

export type PermissionCheckInput = {
  value: string | string[] | undefined;
  any?: boolean;
  not?: boolean;
};

function getAuthzPermissions(): Set<string> {
  const raw = (
    store.state as { AuthzModule?: { permissions?: Set<string> | string[] } }
  ).AuthzModule?.permissions;

  if (raw instanceof Set) {
    return raw;
  }

  if (Array.isArray(raw)) {
    return new Set(raw);
  }

  return new Set<string>();
}

export function evaluatePermission(input: PermissionCheckInput): boolean {
  const needPerm = input.value;

  if (!needPerm || (Array.isArray(needPerm) && needPerm.length === 0)) {
    return true;
  }

  const permissions = getAuthzPermissions();

  let hasPermission = false;

  if (Array.isArray(needPerm)) {
    hasPermission = input.any
      ? needPerm.some((perm) => permissions.has(perm))
      : needPerm.every((perm) => permissions.has(perm));
  } else {
    hasPermission = permissions.has(needPerm);
  }

  if (input.not) {
    hasPermission = !hasPermission;
  }

  return hasPermission;
}

export async function ensurePermissionsAndCheck(
  input: PermissionCheckInput,
): Promise<boolean> {
  await store.dispatch(Actions.LOAD_ABILITIES);
  return evaluatePermission(input);
}

export function watchAbilities(callback: () => void): () => void {
  return store.watch(
    (state) => {
      const authz = (
        state as {
          AuthzModule?: {
            loaded?: boolean;
            version?: string | null;
            permissions?: Set<string>;
          };
        }
      ).AuthzModule;

      return [
        authz?.loaded ?? false,
        authz?.version ?? '',
        authz?.permissions?.size ?? 0,
        ...Array.from(authz?.permissions ?? []).sort(),
      ].join('|');
    },
    callback,
  );
}
